import React, { useState, useCallback } from 'react';
import { Play, X, ExternalLink } from 'lucide-react';
import { featuredTracks, artistInfo } from '../mock';
import { audioManager } from '../lib/audioManager';

const getSpotifyTrackId = (url) => {
  if (!url || url === '#') return null;
  const match = url.match(/track\/([a-zA-Z0-9]+)/);
  return match ? match[1] : null;
};

export const MusicSection = () => {
  const [playingTrackId, setPlayingTrackId] = useState(null);

  const stopSelf = useCallback(() => {
    setPlayingTrackId(null);
    audioManager.clearNowPlaying(stopSelf);
  }, []);

  const handlePlay = (track) => {
    const spotifyId = getSpotifyTrackId(track.streamingLinks.spotify);
    if (spotifyId) {
      if (playingTrackId === track.id) {
        setPlayingTrackId(null);
        audioManager.clearNowPlaying(stopSelf);
      } else {
        audioManager.requestPlayback(stopSelf, {
          title: track.title,
          subtitle: 'Spotify Preview',
        });
        setPlayingTrackId(track.id);
      }
    }
  };

  return (
    <section id="music" className="py-24 bg-black relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-1/3 right-0 w-96 h-96 bg-teal-500 rounded-full filter blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-teal-50 mb-4">
              Featured Music
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-teal-500 to-gold-500 mx-auto mb-6"></div>
            <p className="text-xl text-teal-200 max-w-2xl mx-auto">
              Listen to the latest releases and discover the soulful sound of Brooksgordon
            </p>
          </div>

          {/* Tracks Grid */}
          <div className="grid md:grid-cols-2 gap-8">
            {featuredTracks.map((track) => {
              const spotifyId = getSpotifyTrackId(track.streamingLinks.spotify);
              const isPlaying = playingTrackId === track.id;

              return (
                <div 
                  key={track.id} 
                  className="bg-gradient-to-br from-gray-900 to-teal-950 border border-teal-900/30 rounded-2xl overflow-hidden hover:border-teal-700/50 transition-all duration-300 group"
                >
                  {/* Track Cover */}
                  <div className="relative aspect-square overflow-hidden">
                    <img 
                      src={track.coverArt} 
                      alt={track.title}
                      className={`w-full h-full object-cover transition duration-500 ${isPlaying ? 'scale-110 blur-sm brightness-50' : 'group-hover:scale-110'}`}
                    />

                    {isPlaying && spotifyId ? (
                      /* Spotify Embed Player Overlay */
                      <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80 p-4">
                        <button 
                          onClick={() => setPlayingTrackId(null)}
                          className="absolute top-3 right-3 z-10 w-9 h-9 bg-black/70 hover:bg-black rounded-full flex items-center justify-center text-white transition-colors"
                        >
                          <X size={18} />
                        </button>
                        <div className="w-full h-full max-w-[95%] max-h-[95%] rounded-xl overflow-hidden">
                          <iframe
                            src={`https://open.spotify.com/embed/track/${spotifyId}?utm_source=generator&theme=0`}
                            width="100%"
                            height="100%"
                            frameBorder="0"
                            allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                            loading="lazy"
                            className="rounded-xl"
                            title={`Play ${track.title}`}
                          />
                        </div>
                      </div>
                    ) : (
                      /* Play Button Overlay */
                      <div 
                        className={`absolute inset-0 bg-black/60 transition-opacity duration-300 flex items-center justify-center ${spotifyId ? 'opacity-0 group-hover:opacity-100 cursor-pointer' : 'opacity-0 group-hover:opacity-100'}`}
                        onClick={() => spotifyId && handlePlay(track)}
                      >
                        <div className={`w-20 h-20 ${spotifyId ? 'bg-gold-500 hover:bg-gold-600' : 'bg-gray-600'} rounded-full flex items-center justify-center transform hover:scale-110 transition-all duration-200 shadow-lg`}>
                          <Play className="text-black ml-1" size={32} fill="currentColor" />
                        </div>
                        {spotifyId && (
                          <span className="absolute bottom-4 text-white/80 text-sm font-medium">
                            Click to play preview
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Track Info */}
                  <div className="p-6 space-y-4">
                    <div>
                      <h2 className="text-2xl font-bold text-teal-50 mb-2">{track.title}</h2>
                      <div className="flex items-center justify-between text-sm text-teal-300">
                        <span>{track.artist}</span>
                        <span>{track.releaseYear} • {track.duration}</span>
                      </div>
                    </div>

                    {/* Streaming Links */}
                    <div className="space-y-2">
                      <p className="text-sm text-teal-200 font-medium uppercase tracking-wider">Listen Now</p>
                      <div className="flex flex-wrap gap-2">
                        <a 
                          href={track.streamingLinks.spotify}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 hover:scale-105 flex items-center gap-2"
                        >
                          Spotify
                          <ExternalLink size={14} />
                        </a>
                        <a 
                          href={track.streamingLinks.appleMusic}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-pink-600 hover:bg-pink-700 text-white px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 hover:scale-105 flex items-center gap-2"
                        >
                          Apple Music
                          <ExternalLink size={14} />
                        </a>
                        <a 
                          href={track.streamingLinks.youtube}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 hover:scale-105 flex items-center gap-2"
                        >
                          YouTube
                          <ExternalLink size={14} />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* More Music CTA */}
          <div className="text-center mt-12">
            <p className="text-teal-200 mb-4">Explore the complete discography</p>
            <div className="flex flex-wrap gap-4 justify-center">
              <a 
                href={artistInfo.socialMedia.spotify}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-teal-900/50 hover:bg-teal-900/70 backdrop-blur-sm text-teal-100 px-8 py-3 rounded-full font-semibold transition-all duration-200 border border-teal-700/50 hover:border-teal-600 flex items-center gap-2"
              >
                <img src="https://upload.wikimedia.org/wikipedia/commons/8/84/Spotify_icon.svg" alt="Spotify" className="w-5 h-5" />
                Spotify Profile
              </a>
              <a 
                href={artistInfo.socialMedia.appleMusic}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-teal-900/50 hover:bg-teal-900/70 backdrop-blur-sm text-teal-100 px-8 py-3 rounded-full font-semibold transition-all duration-200 border border-teal-700/50 hover:border-teal-600 flex items-center gap-2"
              >
                Apple Music Profile
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
